import axios from 'axios';
import { BASE_URL, API_URLS } from '../constants/config';
import { refreshTokens, logOut } from './usersActions';
import { checkIfTokenExpired, getTokensFromStore } from '../utilities/tokens';
import { normalizePartners, normalizeHref } from '../utilities/partners';

// Спонсоры лиг. Контракт рутов — PARTNERS_BACKEND.md.
// Запросы возвращают данные напрямую и в redux ничего не пишут.

const authorizedTokens = async (dispatch) => {
    if (checkIfTokenExpired()) {
        await dispatch(refreshTokens());
    }
    return getTokensFromStore();
};

const jsonHeaders = (tokens) => ({
    headers: {
        Authorization: 'Bearer ' + tokens.access_token,
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
});

// Content-Type для multipart браузер выставит сам, вместе с boundary
const formHeaders = (tokens) => ({
    headers: {
        Authorization: 'Bearer ' + tokens.access_token,
        Accept: 'application/json',
    },
});

const handleAuthError = (dispatch, error) => {
    if (error.response && error.response.status === 401) {
        dispatch(logOut());
    }
    throw error;
};

const postJson = (url, body) => async dispatch => {
    const tokens = await authorizedTokens(dispatch);
    return axios.post(BASE_URL.url + url, body, jsonHeaders(tokens))
        .then(res => res.data)
        .catch(error => handleAuthError(dispatch, error));
};

const partnerFields = (data) => ({
    name: data.name.trim(),
    description: (data.description || '').trim(),
    href: normalizeHref(data.href),
});

export const fetchAdminPartners = () => async dispatch => {
    const tokens = await authorizedTokens(dispatch);
    return axios.post(BASE_URL.url + API_URLS.getAdminPartners, {}, jsonHeaders(tokens))
        .then(res => normalizePartners(res.data))
        .catch(error => handleAuthError(dispatch, error));
};

// Логотип при создании обязателен — всегда multipart
export const createPartner = (data) => async dispatch => {
    const tokens = await authorizedTokens(dispatch);
    const form = new FormData();
    Object.entries(partnerFields(data)).forEach(([key, value]) => form.append(key, value));
    form.append('logo', data.logoFile);
    return axios.post(BASE_URL.url + API_URLS.createPartner, form, formHeaders(tokens))
        .then(res => res.data)
        .catch(error => handleAuthError(dispatch, error));
};

// Новый логотип — multipart с полем `logo`, иначе JSON
export const updatePartner = (data) => async dispatch => {
    const tokens = await authorizedTokens(dispatch);
    const fields = { partner_id: data.partner_id, ...partnerFields(data) };
    if (!data.logoFile) {
        return axios.post(BASE_URL.url + API_URLS.updatePartner, fields, jsonHeaders(tokens))
            .then(res => res.data)
            .catch(error => handleAuthError(dispatch, error));
    }
    const form = new FormData();
    Object.entries(fields).forEach(([key, value]) => form.append(key, String(value)));
    form.append('logo', data.logoFile);
    return axios.post(BASE_URL.url + API_URLS.updatePartner, form, formHeaders(tokens))
        .then(res => res.data)
        .catch(error => handleAuthError(dispatch, error));
};

export const deletePartner = (partnerId) =>
    postJson(API_URLS.deletePartner, { partner_id: partnerId });

// Полный список лиг, где показывать спонсора
export const setPartnerLeagues = (partnerId, leagueIds) =>
    postJson(API_URLS.setPartnerLeagues, { partner_id: partnerId, league_ids: leagueIds });

// Полный список спонсоров лиги в порядке показа (см. leaguePartnersPayload)
export const setLeaguePartners = (payload) =>
    postJson(API_URLS.setLeaguePartners, payload);

// Создание или правка спонсора вместе с его лигами. Возвращает id спонсора
export const savePartner = (partner, result) => async dispatch => {
    let id = partner ? partner.id : null;
    if (!partner) {
        const created = await dispatch(createPartner(result));
        id = Number(created && (created.id !== undefined ? created.id : created.partner_id));
        if (!id) throw new Error('Сервер не вернул id нового спонсора');
    } else if (result.fieldsChanged) {
        await dispatch(updatePartner({ partner_id: id, ...result }));
    }
    if (result.leaguesChanged) {
        await dispatch(setPartnerLeagues(id, result.leagueIds));
    }
    return id;
};

// Текст ошибки для админа. 404 — рутов спонсоров на сервере ещё нет
export const partnerErrorText = (error, fallback) => {
    if (error && error.response && error.response.status === 404) {
        return 'На сервере ещё нет раздела спонсоров — он заработает после обновления бэкенда.';
    }
    const data = error && error.response && error.response.data;
    if (typeof data === 'string' && data) return data;
    if (data && (data.message || data.error)) return data.message || data.error;
    return (error && error.message) || fallback;
};
